import { Injectable } from "@angular/core";
import { DateRange } from "@angular/material/datepicker";
import { pushError } from "@app/_helpers/globalErrorHandler";
import { hasPermissionByKey } from "@app/_helpers/permission";
import { useUserRulebreaksParser } from "@app/_helpers/rulebreaks/utils";
import { WorkingHourViolation } from "@app/_helpers/rulebreakUtil";
import { ParsedComegoTime } from "@app/_helpers/types";
import {
	CreateScheduleDayParserValue,
	createDateRangeArray,
	createRxValue,
	createScheduleDayParserFromUser,
	distinctUntilChangedJson,
	fromRxValue,
	getActiveSchedule,
	timeDurationSeconds,
} from "@app/_helpers/utils";
import { ClockService } from "@app/_services/clock.service";
import { disabledFlags } from "@app/shared/time-comego-view/time-comego-view.component";
import { environment } from "@env/environment";
import {
	ComegoQuery,
	ComegoService,
	ComegoStore,
	ComegoTime,
	ComegoTimeType,
	FeedEntry,
	FeedQuery,
	FeedService,
	MyTimesQuery,
	MyTimesService,
	ProjectsQuery,
	ProjectsService,
	Time,
	UserSchedule,
	UserSettings,
	UserSettingsQuery,
	WorkspaceUser,
} from "@timeghost/web-api";
import { MyTimesStore } from "@timeghost/web-api/lib/stores/myTimes/myTimes.store";
import {
	addDays,
	addMonths,
	differenceInDays,
	endOfDay,
	endOfToday,
	endOfISOWeek as endOfWeek,
	getISODay,
	isBefore,
	isFuture,
	isSameDay,
	isSameWeek,
	isValid,
	isWithinInterval,
	roundToNearestMinutes,
	startOfDay,
	startOfToday,
	startOfISOWeek as startOfWeek,
	subDays,
	subMonths,
} from "date-fns/esm";
import sorter from "fluent-sorter";
import { groupBy } from "lodash/fp";
import { MediaObserver } from "ngx-flexible-layout";
import {
	combineLatest,
	distinctUntilChanged,
	filter,
	firstValueFrom,
	from,
	map,
	Observable,
	of,
	shareReplay,
	startWith,
	switchMap,
} from "rxjs";

export type CalendarDayData = {
	date: Date;
	projecthoursTimes: {
		times: Time[];
		totalTime: number;
		feedEntries: FeedEntry[];
		schedule: CreateScheduleDayParserValue;
	};
	workinghoursTimes: {
		times: ComegoTime[];
		violations: WorkingHourViolation[];
		schedule: CreateScheduleDayParserValue;
	};
};

export type CalendarWeekData = {
	range: DateRange<Date>;
	days: CalendarDayData[];
};

export interface CalendarScheduleMetric {
	used: number;
	percent: number;
	graphState: "primary" | "accent" | "success";
}

export interface CalendarWeekScheduleSummary {
	max: number;
	workinghours: CalendarScheduleMetric;
	projecthours: CalendarScheduleMetric;
}

export type TimesListViewDay = {
	date: Date;
	id: string;
	comego: ParsedComegoTime[];
	projectTimes: Time[];
	schedule:
		| (CreateScheduleDayParserValue & {
				isPastSchedule?: boolean;
				percent?: number;
				graphState?: "primary" | "accent" | "success";
				pending?: number;
		  })
		| null;
	editable: boolean;
	deleteable: boolean;
	meta: Record<string, unknown>;
	sum: { [K in ComegoTimeType]: number } & { realBreak: number };
	allowEdit: boolean;
	isPending: boolean;
};

export type TimesListView = TimesListViewDay[] | null;

function computeDaySum(times: ComegoTime[], userSettings: UserSettings) {
	const sum = { pause: 0, realBreak: 0, work: 0, absence: 0 };
	const wh = userSettings.workspace.schedules.workspace["workinghours"] as { minimumBreakDurationForCredit?: number };
	const shortBreakDuration = (wh?.minimumBreakDurationForCredit ?? 0.25) * 3600;

	for (const r of times) {
		const timeDiff = timeDurationSeconds(r);
		if (timeDiff) sum[r.type] += timeDiff;
		if (timeDiff && r.type === "pause" && timeDiff > shortBreakDuration) {
			sum.realBreak += timeDiff;
		}
	}
	return sum;
}

function enrichListViewSchedule(
	schedule: CreateScheduleDayParserValue | null | undefined,
	date: Date,
	startPending: Date
): TimesListViewDay["schedule"] {
	if (!schedule) return null;
	const isPastSchedule = startPending.getTime() >= date.getTime();
	const pastFailure = isPastSchedule && schedule.usage < schedule.cap;
	return {
		...schedule,
		isPastSchedule,
		percent: schedule.usage / schedule.cap,
		graphState: pastFailure ? "accent" : schedule.usage < schedule.cap ? "accent" : "success",
		pending: schedule.cap - schedule.usage,
	};
}

export function mapCalendarDaysToListView(days: CalendarDayData[], userSettings: UserSettings): TimesListView {
	const startPending = startOfDay(new Date());
	const canUpdate = hasPermissionByKey(userSettings, "groupsCanComegoUpdateTimes");
	const now = new Date();

	const result = days
		.filter((day) => day.workinghoursTimes.times.length > 0)
		.map((day): TimesListViewDay => {
			const rawTimes = day.workinghoursTimes.times;
			const comego = [...rawTimes]
				.map((r, i, arr) => {
					const isValidEnd = r.end && isValid(new Date(r.end));
					const timeDiff = timeDurationSeconds(r);
					const recording = !!(r as ComegoTime & { recording?: boolean }).recording;
					return {
						...r,
						start: new Date(r.start),
						end: isValidEnd ? new Date(r.end!) : null,
						timeDiff,
						meta: {
							paused: r.type === "pause",
							resume: r.type === "work",
							start: i === 0,
							stopped: i === arr.length - 1,
							recording,
							single: false,
							type: r.type,
						},
					} as ParsedComegoTime;
				})
				.sort(
					sorter((x: ParsedComegoTime) => x.start, "desc").thenBy(
						(x: ParsedComegoTime) => (!x.end ? now : x.end),
						"desc"
					)
				);

			const meta: Record<string, unknown> = {};
			if (day.workinghoursTimes.violations.length) {
				meta.workinghoursErrors = day.workinghoursTimes.violations;
			}

			return {
				date: day.date,
				id: day.date.toISOString(),
				comego,
				projectTimes: day.projecthoursTimes.times,
				schedule: enrichListViewSchedule(day.workinghoursTimes.schedule, day.date, startPending),
				editable: canUpdate,
				deleteable: canUpdate,
				meta,
				sum: computeDaySum(rawTimes, userSettings),
				allowEdit: true,
				isPending: !isBefore(day.date, startPending),
			};
		})
		.sort(sorter((x) => x.date, "asc"));

	return result.length ? result : null;
}

function sortByStartAsc<T extends { start?: string | Date | null }>(a: T, b: T): number {
	return new Date(a.start!).getTime() - new Date(b.start!).getTime();
}

function dayKey(date: Date | string): number {
	return startOfDay(new Date(date)).getTime();
}

function fingerprintEntries(items: Array<Record<string, any>>): string {
	return items.map((t) => `${t["id"] ?? ""}:${t["start"] ?? ""}:${t["end"] ?? ""}:${t["_ts"] ?? ""}`).join(",");
}

function fingerprintCalendarDays(days: CalendarDayData[]): string {
	return days
		.map((d) => {
			const p = d.projecthoursTimes;
			const w = d.workinghoursTimes;
			return [
				d.date.getTime(),
				fingerprintEntries(p.times),
				fingerprintEntries(p.feedEntries),
				fingerprintEntries(w.times),
				w.violations.length,
				p.schedule?.usage ?? 0,
				p.schedule?.cap ?? 0,
				w.schedule?.usage ?? 0,
				w.schedule?.cap ?? 0,
			].join("#");
		})
		.join("|");
}

@Injectable({
	providedIn: "root",
})
export class TimerService {
	constructor(
		private userSettingsQuery: UserSettingsQuery,
		private myTimesService: MyTimesService,
		private myTimesQuery: MyTimesQuery,
		private comegoService: ComegoService,
		private comegoQuery: ComegoQuery,
		private projectsQuery: ProjectsQuery,
		private projectsService: ProjectsService,
		private feedService: FeedService,
		private feedQuery: FeedQuery,
		private clock: ClockService
	) {}

	readonly range = createRxValue<DateRange<Date>>(new DateRange(startOfWeek(new Date()), endOfWeek(new Date())));

	readonly minRangeStart = subMonths(startOfToday(), 12);
	readonly maxRangeStart = addMonths(endOfToday(), 6);
	readonly viewDate$ = this.range.asObservable().pipe(filter((x) => !!x?.start && !!x.end));
	readonly viewDate$status = this.viewDate$.pipe(
		map(({ start, end }) => {
			const sameDay = isSameDay(start, end),
				sameWeek = isSameWeek(start, end),
				isFutureTime = isFuture(end),
				isValidRange = isWithinInterval(isFutureTime ? end : start, {
					start: this.minRangeStart,
					end: this.maxRangeStart,
				});
			return {
				start,
				end,
				isToday: isSameDay(start, end) && isSameDay(start, new Date()),
				sameDay,
				sameWeek,
				isValidRange,
				isFuture: isFutureTime,
			};
		})
	);
	readonly projecthoursEnabled = fromRxValue(
		this.userSettingsQuery.select().pipe(map((x) => !!x?.workspace?.settings?.projecthoursEnabled))
	);
	readonly workinghoursEnabled = fromRxValue(
		this.userSettingsQuery.select().pipe(map((x) => !!x?.workspace?.settings?.workinghoursEnabled))
	);
	readonly workinghoursCanCreate = fromRxValue(
		this.userSettingsQuery.select().pipe(map((x) => hasPermissionByKey(x, "groupsCanComegoCreateTimes" as any)))
	);
	readonly workinghoursCanCreateAbsences = fromRxValue(
		this.userSettingsQuery.select().pipe(map((x) => hasPermissionByKey(x, "groupsCanCreateAbsences" as any)))
	);
	readonly workinghoursCanUpdate = fromRxValue(
		this.userSettingsQuery.select().pipe(map((x) => hasPermissionByKey(x, "groupsCanComegoUpdateTimes" as any)))
	);

	prev() {
		const viewDate = this.range.value;
		let days = differenceInDays(viewDate.end, viewDate.start).clampThis(0) + 1;
		return this.range.update(new DateRange<Date>(subDays(viewDate.start, days), subDays(viewDate.end, days)));
	}
	today() {
		const now = new Date();
		return this.range.update(new DateRange<Date>(startOfWeek(now), endOfWeek(now)));
	}
	next() {
		const viewDate = this.range.value;
		let days = differenceInDays(viewDate.end, viewDate.start).clampThis(0) + 1;
		return this.range.update(new DateRange<Date>(addDays(viewDate.start, days), addDays(viewDate.end, days)));
	}
	selectWeek(date: Date) {
		const start = startOfWeek(date),
			end = endOfWeek(date);
		return this.range.update(new DateRange<Date>(start, end));
	}
	async fetchBetween(start: Date, end: Date) {
		const user = this.userSettingsQuery.getValue();
		if (this.workinghoursEnabled.value) {
			(this.comegoQuery.__store__ as ComegoStore).setLoading(true);
			const comego = await this.comegoService.getBetween(start, end, user.id).catch((err) => {
				pushError(err);
				return [] as ComegoTime[];
			});
			(this.comegoQuery.__store__ as ComegoStore).upsertMany(comego);
			(this.comegoQuery.__store__ as ComegoStore).setLoading(false);
		}
		if (this.projecthoursEnabled.value) {
			(this.myTimesQuery.__store__ as MyTimesStore).setLoading(true);
			const times = await firstValueFrom(
				this.myTimesService.adapter.get<Time[]>(
					environment.serverUrl +
						`/get/times?$filter=start ge '${start.toISOString()}' and start le '${end.toISOString()}' and user__id eq '${user.id}'`
				)
			).catch((err) => {
				pushError(err);
				return [] as Time[];
			});
			(this.myTimesQuery.__store__ as MyTimesStore).upsertMany(times);
			await this.ensureMissingProjectsLoaded(times);
			(this.myTimesQuery.__store__ as MyTimesStore).setLoading(false);
			await this.feedService.load(start, end);
		}
	}

	private async ensureMissingProjectsLoaded(times: Time[]) {
		const needsCompletedProjects = times?.some((t) => t.project?.id && !this.projectsQuery.hasEntity(t.project.id));
		if (!needsCompletedProjects) return;
		await firstValueFrom(this.projectsService.get(true));
	}

	readonly rangeFetch$ = this.viewDate$.pipe(
		distinctUntilChangedJson(({ start, end }) => ({
			start: start?.toISOString(),
			end: end?.toISOString(),
		})),
		switchMap(({ start, end }) => from(this.fetchBetween(start, end))),
		shareReplay(1)
	);

	readonly isLoading$ = combineLatest([
		this.myTimesQuery.selectLoading().pipe(startWith(false)),
		this.comegoQuery.selectLoading().pipe(startWith(false)),
		this.feedQuery.selectLoading().pipe(startWith(false)),
	]).pipe(map(([a, b, c]) => a || b || c));
	readonly rulebreaksParser = useUserRulebreaksParser();

	private filterTimesInRange<T extends { start?: string | Date | null }>(items: T[], range: DateRange<Date>): T[] {
		return items
			.filter((t) => t.start && isValid(new Date(t.start)) && isWithinInterval(new Date(t.start), range))
			.sort(sortByStartAsc);
	}

	private filterFeedInRange(entries: FeedEntry[], range: DateRange<Date>): FeedEntry[] {
		return (entries ?? [])
			.filter((e) => {
				const start = new Date(e.start);
				return (
					isValid(start) &&
					isWithinInterval(start, { start: startOfDay(range.start), end: endOfDay(range.end) })
				);
			})
			.sort(sortByStartAsc);
	}

	private async buildWeekData(
		range: DateRange<Date>,
		myTimes: Time[],
		comegoTimes: ComegoTime[],
		feedEntries: FeedEntry[]
	): Promise<CalendarDayData[]> {
		const filteredMyTimes = this.filterTimesInRange(myTimes, range);
		const filteredComegoTimes = this.filterTimesInRange(comegoTimes, range);
		const filteredFeedEntries = this.filterFeedInRange(feedEntries, range);

		const myTimesByDay = groupBy((t: Time) => dayKey(t.start!), filteredMyTimes);
		const comegoByDay = groupBy((t: ComegoTime) => dayKey(t.start!), filteredComegoTimes);
		const feedByDay = groupBy((e: FeedEntry) => dayKey(e.start), filteredFeedEntries);

		const dayDates = createDateRangeArray(range.start, range.end, false)[0].map(([date]) => date);

		const userSettings = this.userSettingsQuery.getValue();
		const user = userSettings.workspace.users.find((u) => u.id === userSettings.id) as WorkspaceUser;
		const parsedDays = await this.rulebreaksParser(user, userSettings, filteredComegoTimes, {
			from: range.start,
			to: range.end,
		});
		const parsedByDay = new Map(parsedDays.map((p) => [startOfDay(p.date).getTime(), p]));

		return dayDates.map((date) => {
			const key = dayKey(date);
			const dayMyTimes = myTimesByDay[key] ?? [];
			const dayComegoTimes = (comegoByDay[key] ?? []).map((t) => ({
				...t,
				timeDiff: timeDurationSeconds(t),
			}));
			const dayFeedEntries = feedByDay[key] ?? [];
			const scheduleParser = createScheduleDayParserFromUser(userSettings, date, undefined, true);
			const schedule = scheduleParser(
				dayComegoTimes.filter((t) => t.type !== "pause") as unknown as Time[],
				date
			);
			const parsedDay = parsedByDay.get(key);
			const totalTime = dayMyTimes.reduce((acc, time) => acc + time.timeDiff, 0);

			return {
				date,
				projecthoursTimes: {
					times: [...dayMyTimes]
						.map((t) =>
							t.end == null
								? {
										...t,
										end: roundToNearestMinutes(Date.now(), { nearestTo: 1 }).toISOString(),
										recording: true,
									}
								: t
						)
						.sort(sortByStartAsc),
					totalTime,
					feedEntries: [...dayFeedEntries].sort(sortByStartAsc),
					schedule,
				},
				workinghoursTimes: {
					times: [...(parsedDay?.times ?? [])]
						.map((t) =>
							t.end == null
								? {
										...t,
										end: roundToNearestMinutes(Date.now(), { nearestTo: 1 }).toISOString(),
										recording: true,
									}
								: t
						)
						.sort(sortByStartAsc),
					violations: parsedDay?.issues ?? [],
					schedule,
				},
			};
		});
	}

	private mapTimesWithTimeDiff<T extends { start: string; end?: string | null }>(items: T[]) {
		return items.map((x) => {
			const end = new Date(x.end ?? Date.now());
			return { ...x, timeDiff: (end.getTime() - new Date(x.start).getTime()) / 1000 || 0 };
		});
	}

	private aggregateScheduleUsage<T extends { start: string; type?: string }>(
		range: DateRange<Date>,
		user: UserSettings,
		times: T[],
		filterFn?: (x: T) => boolean
	): { used: number; max: number } | null {
		return createDateRangeArray(range.start, range.end, false)[0].reduce(
			(acc, [d]) => {
				const scheduleParser = createScheduleDayParserFromUser(user, d, undefined, true);
				if (!scheduleParser) return acc;
				const data = scheduleParser(
					times.filter((x) => {
						const dt = new Date(x.start);
						if (!isValid(dt)) return false;
						if (filterFn && !filterFn(x)) return false;
						return isWithinInterval(dt, range);
					}) as unknown as Time[],
					d
				);
				if (!data.enabled) return acc;
				if (!acc) {
					acc = { used: data.usage, max: data.cap };
				} else {
					acc.used += data.usage;
					acc.max += data.cap;
				}
				return acc;
			},
			null as { used: number; max: number } | null
		);
	}

	private buildScheduleMetric(used: number, max: number, isPastSchedule: boolean): CalendarScheduleMetric {
		const pastFailure = isPastSchedule && used < max;
		const percent = max > 0 ? used / max : 0;
		return {
			used,
			percent,
			graphState: pastFailure ? "primary" : used < max ? "accent" : "success",
		};
	}

	private buildWeekScheduleSummary(
		range: DateRange<Date>,
		user: UserSettings,
		myTimes: Time[],
		comegoTimes: ComegoTime[]
	): CalendarWeekScheduleSummary {
		const isPastSchedule = Date.now() > range.end.getTime();
		const workingAgg = this.aggregateScheduleUsage(
			range,
			user,
			this.mapTimesWithTimeDiff(comegoTimes),
			(x) => !disabledFlags.includes(x.type)
		);
		const projectAgg = this.aggregateScheduleUsage(range, user, this.mapTimesWithTimeDiff(myTimes));
		const max = workingAgg?.max ?? projectAgg?.max ?? 0;
		return {
			max,
			workinghours: this.buildScheduleMetric(workingAgg?.used ?? 0, max, isPastSchedule),
			projecthours: this.buildScheduleMetric(projectAgg?.used ?? 0, max, isPastSchedule),
		};
	}

	private readonly comegoFromCurrentUser$ = this.comegoQuery.selectAllFromCurrentUser().pipe(shareReplay(1));

	private hasActiveRecordingInRange(range: DateRange<Date>): Observable<boolean> {
		return combineLatest([
			this.myTimesQuery.selectAll().pipe(map((items) => this.filterTimesInRange(items, range))),
			this.comegoFromCurrentUser$.pipe(map((items) => this.filterTimesInRange(items, range))),
		]).pipe(
			map(([times, comego]) => times.some((t) => t.end == null) || comego.some((t) => t.end == null)),
			distinctUntilChanged()
		);
	}
	readonly hasActiveWorkinghourRecording$ = fromRxValue(
		this.comegoFromCurrentUser$.pipe(map((items) => items.some((t) => t.end == null && t.type === "work")))
	);
	readonly hasActiveProjecthourRecording$ = fromRxValue(
		this.myTimesQuery.selectAll().pipe(map((items) => items.some((t) => t.end == null)))
	);

	private recordingTick$(range: DateRange<Date>): Observable<unknown> {
		return this.hasActiveRecordingInRange(range).pipe(switchMap((active) => (active ? this.clock.now$ : of(null))));
	}

	readonly times$: Observable<CalendarDayData[]> = this.viewDate$.pipe(
		switchMap((range) =>
			combineLatest([
				of(range),
				this.myTimesQuery.selectAll().pipe(map((items) => this.filterTimesInRange(items, range))),
				this.comegoFromCurrentUser$.pipe(map((items) => this.filterTimesInRange(items, range))),
				this.feedQuery.selectAll().pipe(map((items) => this.filterFeedInRange(items ?? [], range))),
				this.recordingTick$(range),
			]).pipe(
				switchMap(([r, myTimes, comegoTimes, feedEntries]) =>
					from(this.buildWeekData(r, myTimes, comegoTimes, feedEntries))
				)
			)
		),
		distinctUntilChanged((prev, next) => fingerprintCalendarDays(prev) === fingerprintCalendarDays(next)),
		shareReplay(1)
	);
	readonly times$listView$: Observable<TimesListView> = combineLatest([
		this.times$,
		this.userSettingsQuery.select(),
	]).pipe(
		map(([days, userSettings]) => mapCalendarDaysToListView(days, userSettings)),
		shareReplay(1)
	);
	readonly schedule$ = this.viewDate$.pipe(
		switchMap((range) =>
			combineLatest([
				of(range),
				this.userSettingsQuery.select(),
				this.myTimesQuery.selectAll().pipe(map((items) => this.filterTimesInRange(items, range))),
				this.comegoFromCurrentUser$.pipe(map((items) => this.filterTimesInRange(items, range))),
				this.recordingTick$(range),
			]).pipe(
				map(([r, user, myTimes, comegoTimes]) => this.buildWeekScheduleSummary(r, user, myTimes, comegoTimes))
			)
		),
		distinctUntilChanged(
			(a, b) =>
				a.max === b.max &&
				a.workinghours.used === b.workinghours.used &&
				a.projecthours.used === b.projecthours.used
		),
		shareReplay(1)
	);
}
